import { useEffect } from "react";
import { Link } from "react-router-dom";
import "./PrivacyPolicy.css";

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
    const previousTitle = document.title;
    document.title = "Privacy Policy — Revasa La Valora";
    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <div className="pp" data-testid="privacy-policy">
      <header className="pp-mast">
        <div className="pp-brand">
          <em>La</em> <span>VALORA</span>
        </div>
        <Link className="pp-back" to="/">
          ← Back to site
        </Link>
      </header>

      <main className="pp-doc">
        <p className="pp-eyebrow">Legal</p>
        <h1>
          Privacy <em>Policy</em>
        </h1>
        <p className="pp-meta">
          Effective date: 30 July 2026 &nbsp;·&nbsp; Last updated: 30 July 2026
        </p>
        <p className="pp-lede">
          This website is operated by Keyon Properties &amp; Solutions Pvt.
          Ltd., an authorized channel partner, and presents information about
          the Revasa La Valora project. This page sets out our disclaimer and
          how we handle the information you share with us.
        </p>

        <hr className="pp-hr" />

        <section>
          <h2>
            <span className="pp-num">01</span> Disclaimer
          </h2>
          <p>
            This website is only for the purpose of providing information
            regarding real estate projects in different regions. By accessing
            this website, the viewer confirms that the information including
            brochures and marketing collaterals on this website is solely for
            informational purposes and the viewer has not relied on this
            information for making any booking or purchase in any project of the
            company.
          </p>
          <p>
            Nothing on this website constitutes advertising, marketing, booking,
            selling or an offer for sale, or invitation to purchase a unit in
            any project by the company. The company is not liable for any
            consequence of any action taken by the viewer relying on such
            material or information on this website.
          </p>
          <p>
            Please also note that the company has not independently verified the
            information and the compliances of the projects listed herein. You
            should make yourself aware about the RERA (Real Estate Regulation
            Act, 2016) registration status and approvals of the listed real
            estate projects before purchasing property, and verify all details
            directly with the developer.
          </p>
          <div className="pp-note">
            This site is for information purposes only and should{" "}
            <strong>not</strong> be treated as the official website of the
            developer. Revasa La Valora is developed by{" "}
            <strong>Reliance Builders</strong>, an independent Hyderabad-based
            developer established in 1988, which is not affiliated with Reliance
            Industries Ltd., Jio, Reliance Retail, Reliance Realty, or the
            Mukesh Ambani group.
          </div>
        </section>

        <section>
          <h2>
            <span className="pp-num">02</span> Summary
          </h2>
          <p>
            We at <strong>Keyon Properties</strong> work as an authorized
            channel partner with reputed real estate developers. We promote
            their projects and follow up on enquiries received through various
            online mediums, so that prospective buyers can visit the respective
            projects and receive proper assistance from our trained and
            qualified sales managers through to a decision.
          </p>
          <p>
            We are remunerated by way of brokerage from the developer.{" "}
            <strong>No fees are charged to the end user</strong> for our
            services.
          </p>
        </section>

        <section>
          <h2>
            <span className="pp-num">03</span> Governing law
          </h2>
          <p>
            Our company is registered with the{" "}
            <strong>Telangana Real Estate Regulatory Authority (TG-RERA)</strong>
            , which governs the real estate sector in Telangana, as a real
            estate agent under Registration No.{" "}
            <strong>A02400001347</strong>. Under this registration we are
            permitted to facilitate the marketing and sale of real estate
            projects registered under RERA.
          </p>
          <p>
            This policy is governed by the laws of India, and any disputes are
            subject to the exclusive jurisdiction of the courts at Hyderabad,
            Telangana. For visitors in India, we handle personal data in line
            with the{" "}
            <strong>Digital Personal Data Protection Act, 2023</strong> and
            applicable rules.
          </p>
        </section>

        <section>
          <h2>
            <span className="pp-num">04</span> Privacy policy
          </h2>
          <p>
            In our endeavor and commitment of protecting your personal
            information, we have designed this comprehensive privacy policy.
            This is to keep your interests and information safe on our website.
          </p>
        </section>

        <section>
          <h2>
            <span className="pp-num">05</span> Updation of privacy policy
          </h2>
          <p>
            This privacy policy is subject to undergo change and review without
            any prior notice or approval. So to keep yourself updated on the
            changes introduced, please keep visiting and reviewing the terms and
            conditions of this privacy policy.
          </p>
        </section>

        <section>
          <h2>
            <span className="pp-num">06</span> User information
          </h2>
          <p>
            By using our website, you agree to abide by the rules laid out by us
            and consent to collection and use of all such information that you
            may furnish to, or through, our website. In some cases, while you
            visit our website, you may not need to provide any personal
            information. But in certain instances, we must have your personal
            information in order for us to grant you access to some of the links
            or sections of the site — such pages may ask for your{" "}
            <strong>name, e-mail address and phone number</strong>, along with
            any preferences such as configuration or budget that you choose to
            share.
          </p>
          <p>
            The information furnished by you is used to provide relevant
            products and services, to acknowledge receipt of your communication,
            to arrange site visits, and to send out information and updates to
            you. You have the option of requesting removal from our mailing
            list. We do not sell or give away your personal information to any
            third party, except that your enquiry is shared with the developer,{" "}
            <strong>Reliance Builders</strong>, and its authorized
            representatives so that it can be acted upon, and with the service
            providers who operate this website and our sales process on our
            behalf under obligations of confidentiality.
          </p>
          <div className="pp-note">
            By submitting your details, you consent to be contacted by Keyon
            Properties and its authorized representatives regarding this project
            by <strong>phone call, SMS, WhatsApp and email</strong>,{" "}
            <strong>
              even if your number is registered on the DND / NCPR registry
            </strong>
            . You may withdraw this consent at any time by writing to us at the
            contact details below.
          </div>
        </section>

        <section>
          <h2>
            <span className="pp-num">07</span> Security
          </h2>
          <p>
            To ensure security while transferring sensitive information, all the
            ongoing transmissions between client and server are encrypted using
            advanced and standard protocols. We also practice restricted access
            by employees and hold them to high levels of confidentiality.
            However, no method of transmission over the internet is completely
            secure, and we cannot guarantee absolute security.
          </p>
        </section>

        <section>
          <h2>
            <span className="pp-num">08</span> Use of cookies
          </h2>
          <p>
            We may use cookies for security, session continuity, customization
            and measurement purposes, including through{" "}
            <strong>Google Analytics</strong>, <strong>Google Ads</strong>{" "}
            conversion tracking and the{" "}
            <strong>Meta (Facebook) Pixel</strong>. In case of a user opting to
            reject a cookie, he or she may not be able to gain access to some of
            the limited services or use some features of the site.
          </p>
          <p>
            You can control or block cookies through your browser settings, opt
            out of Google Analytics using Google's{" "}
            <a
              href="https://tools.google.com/dlpage/gaoptout"
              target="_blank"
              rel="noopener noreferrer"
            >
              browser add-on
            </a>
            , and manage ad personalization via{" "}
            <a
              href="https://myadcenter.google.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Google Ad Settings
            </a>
            .
          </p>
        </section>

        <section>
          <h2>
            <span className="pp-num">09</span> Contact us
          </h2>
          <p>
            In case of any queries or suggestions regarding this privacy
            statement or your dealings with this website, please contact:
          </p>
          <div className="pp-card">
            <h3>Keyon Properties &amp; Solutions Pvt. Ltd.</h3>
            <span className="pp-label">Phone</span>
            <p>
              <a href="tel:+919030145871">+91 9030145871</a>
            </p>
            <span className="pp-label">TG-RERA Agent Reg. No.</span>
            <p>A02400001347</p>
          </div>
          <div className="pp-note">
            Project references — <strong>Revasa La Valora</strong> · Project
            RERA No.: P01100010542 · HMDA Permission No.:
            012817/LO/HMDA/3450/SKP/2024 · Developed by Reliance Builders,
            Kardanur, Telangana 502300.
          </div>
        </section>
      </main>

      <footer className="pp-foot">
        <span>
          &copy; {new Date().getFullYear()} La Valora. Operated by Keyon
          Properties, an authorized channel partner.
        </span>
        <span className="pp-val">Value Built on Values</span>
      </footer>
    </div>
  );
}
